<template>
  <div class="configuration-view">
    <div class="aside-panel">
      <div class="panel-content">
        <div class="heading">
          <h2 v-if="currentExport.type !== ExportType.Simplified" @click="editConfig">{{ $t('studyExport') }} <i class="icon-cogfilled" /></h2>
        </div>
        <div v-if="currentExportLayout" class="panel-summary">
          <div class="introduction-summary">
            <div class="index-heading" @click="introductionOpened = !introductionOpened">
              <div class="index-icon"></div>
              <div class="index-title">{{ $t('exportIntroduction') }}</div>
              <span class="icon-container" :class="{ reversed: introductionOpened }">
                <i class="icon-arrow_down" />
              </span>
            </div>
            <div v-if="introductionOpened" class="index-container">
              <div v-for="index in introductionIndex" :key="index" class="index-item">
                {{ index }}
              </div>
            </div>
          </div>
          <SiteSummary
            v-if="actualSites.length > 1 || currentExport.type === ExportType.Simplified"
            :key="0"
            :site-idx="0"
            :active-section="activeSection"
            :data="mainSynthesis"
          />
          <template v-if="currentExport.type !== ExportType.Simplified">
            <SiteSummary v-for="(site, idx) in actualSites" :key="site.id" :site-idx="idx + 1" :active-section="activeSection" :data="site" />
          </template>
          <div class="info-box">
            {{ $t('exportTuto') }}
          </div>
          <div class="global-comment" @click="updateActiveSection('global-comment')">
            <span>{{ globalComment ? $t('report.finalComment') : $t('report.addFinalComment') }}</span>
            <i v-if="globalComment" class="icon-bubble"></i>
          </div>
        </div>
      </div>
      <div class="cta-container">
        <button class="study-button secondary" @click="saveExportLayout">{{ $t('save') }}</button>
        <button class="study-button main" @click="printExport">{{ $t('exportStudy') }}</button>
      </div>
    </div>
    <div v-if="loaded" class="preview-container">
      <div v-if="isIE" class="loading-message">Aperçu non disponible sur Internet Explorer</div>
      <Report v-if="!isIE" class="report" />
      <div v-if="activeSection.siteIdx !== null" class="comment-section">
        <div class="comment-title-container" @click="updateActiveSection(activeSection)">
          <span class="comment-title">
            {{ activeSection.siteIdx > 0 ? currentExportLayout.sites[activeSection.siteIdx].name : $t('sections.mainSynthesis') }}
            -
            {{
              $t(
                `transportMode.${transportMode(currentExportLayout.sites[Math.max(activeSection.siteIdx, 0)].transports[activeSection.transportIdx].type).key}`,
              )
            }}
            -
            {{
              $t(
                `sections.${
                  currentExportLayout.sites[Math.max(activeSection.siteIdx, 0)].transports[activeSection.transportIdx].sections[activeSection.sectionIdx].name
                }`,
              )
            }}
          </span>
          <span class="comment-character-counter"
            ><span :class="{ overflow: charCounter >= 1000 }">{{ charCounter }}</span> / 1000</span
          >
        </div>
        <textarea
          v-if="activeSection.siteIdx >= 0"
          v-model="currentExportLayout.sites[activeSection.siteIdx].transports[activeSection.transportIdx].sections[activeSection.sectionIdx].comment"
          class="comment-area"
          maxlength="1000"
          :placeholder="$t('report.addComment')"
        />
      </div>

      <div v-if="activeSection.globalComment" class="comment-section">
        <div class="comment-title-container" @click="updateActiveSection('global-comment')">
          <span class="comment-title">
            {{ $t('report.finalComment') }}
          </span>
          <span class="comment-character-counter"
            ><span :class="{ overflow: globalComment.length >= 1000 }">{{ globalComment.length }}</span> / 1000</span
          >
        </div>
        <textarea v-model="globalComment" class="comment-area" maxlength="1000" :placeholder="$t('report.addComment')" @keyup="updateGlobalComment" />
      </div>
    </div>
    <div class="filter-panel">
      <div class="info-block">
        <div v-if="name" class="title">{{ $t('studyName') }} :</div>
        <div v-if="name" class="text">{{ name }}</div>
        <div v-if="currentExport" class="logo-container" :class="{ filled: logo }">
          <div v-if="logo" class="logo" :style="{ backgroundImage: `url(${logoUrl})` }">
            <div class="close" @click="deleteLogo">✕</div>
          </div>
          <div v-else class="logo-input-container">
            <div class="add-logo-label">{{ $t('report.addLogo') }}</div>
            <input name="clientLogo" type="file" class="logo-input" :placeholder="$t('clientLogo')" @change="uploadLogo($event.target.files)" />
          </div>
        </div>

        <div v-if="study" class="title">{{ $t('client') }} :</div>
        <div v-if="study" class="text">{{ study.client_name }}</div>

        <div v-if="currentExportLayout" class="title">{{ $t('exportDate') }} :</div>
        <div v-if="currentExportLayout" class="text">{{ formatDate(currentExportLayout.exported_at * 1000) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/libraries/formatter';
import TransportMode from '@/pure/TransportMode';
import SiteSummary from '@/components/Export/pure/SiteSummary';
import Report from '@/views/Report';
import { eventBus } from '@/eventBus';
import ExportType from '@/pure/ExportType';

export default {
  components: { SiteSummary, Report },

  data() {
    return {
      formatDate,
      activeSection: {
        siteIdx: null,
        transportIdx: null,
        sectionIdx: null,
      },
      loading: false,
      introductionOpened: false,
      name: null,
      logo: null,
      logoUrl: null,
      error: null,
      globalComment: '',
    };
  },

  computed: {
    ...mapGetters({
      currentExport: 'currentExport',
      currentExportLayout: 'currentExportLayout',
      globalFilters: 'globalFilters',
      userLanguage: 'userLanguage',
      study: 'study',
      globalCommentStore: 'globalComment',
    }),

    ExportType() {
      return ExportType;
    },

    charCounter() {
      const char =
        (this.currentExportLayout.sites[this.activeSection.siteIdx].transports[this.activeSection.transportIdx].sections[this.activeSection.sectionIdx]
          .comment &&
          this.currentExportLayout.sites[this.activeSection.siteIdx].transports[this.activeSection.transportIdx].sections[this.activeSection.sectionIdx]
            .comment) ||
        '';
      return char.length + (char.match(/\n/g) || []).length * 89;
    },

    isIE() {
      const ua = navigator.userAgent;
      /* MSIE used to detect old browsers and Trident used to newer ones */
      return ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
    },

    sites() {
      return this.currentExportLayout.sites;
    },

    introductionIndex() {
      if (this.currentExportLayout.type === ExportType.Simplified) {
        return [this.$t('report.studyParameters'), this.$t('report.currentSitesTargetSites')];
      }
      return [
        this.$t('report.studyParameters'),
        this.$t('report.currentSitesTargetSites'),
        this.$t('report.collaboratorSource'),
        this.$t('report.collaboratorSourceByRegion'),
      ];
    },

    mainSynthesis() {
      const mainSynthesis = this.sites.find(site => site.id === 0);

      mainSynthesis.transports = (mainSynthesis.transports || []).map(transport => {
        if (transport.type === 1) {
          if (this.currentExportLayout.public_transport_type === 2) transport.type = 7;
          if (this.currentExportLayout.public_transport_type === 3) transport.type = 8;

          if (this.currentExportLayout.public_transport_type === 4) transport.type = 9;
          if (this.currentExportLayout.public_transport_type === 5) transport.type = 10;
        }

        return transport;
      });

      return {
        id: 0,
        name: this.$t('sections.mainSynthesis'),
        type: 0,
        transports: mainSynthesis && mainSynthesis.transports ? mainSynthesis.transports : this.sites[0].transports,
      };
    },

    actualSites() {
      const actualSites = this.currentExportLayout.sites.filter(site => site.type === 1);

      actualSites.forEach(site => {
        site.transports = (site.transports || []).map(transport => {
          if (transport.type === 1) {
            if (this.currentExportLayout.public_transport_type === 2) transport.type = 7;
            if (this.currentExportLayout.public_transport_type === 3) transport.type = 8;

            if (this.currentExportLayout.public_transport_type === 4) transport.type = 9;
            if (this.currentExportLayout.public_transport_type === 5) transport.type = 10;
          }

          return transport;
        });
      });

      return actualSites.filter(site => site.id !== 0);
    },

    targetSites() {
      return this.currentExportLayout.sites.filter(site => site.type === 2);
    },

    loaded() {
      return this.currentExportLayout && this.study;
    },
  },

  created() {
    if (!this.currentExportLayout) {
      const id = this.$route.params.studyId;
      this.$store.dispatch('getExportLayout', { id }).then(data => {
        const id = data.study_public_id;
        this.$store.dispatch('getStudyResult', { id });
        this.$store.dispatch('getStudyLightview', { id: data.study_public_id }).then(lightview => {
          this.name = lightview.name;
        });
      });
    }
  },

  mounted() {
    eventBus.on('selectSection', this.updateActiveSection);

    this.logo = !!this.currentExport.logo_url;
    this.logoUrl = this.currentExport.logo_url;

    this.globalComment = this.globalCommentStore || '';
  },

  beforeUnmount() {
    eventBus.off('selectSection', this.updateActiveSection);
    this.$store.dispatch('clearExportLayout');
  },

  methods: {
    transportMode(transportType) {
      return TransportMode.find(mode => mode.index === transportType);
    },

    updateGlobalComment() {
      this.$store.dispatch('updateGlobalComment', { comment: this.globalComment });
    },

    updateActiveSection(data) {
      if (data === 'global-comment') {
        this.activeSection = {
          globalComment: true,
          siteIdx: null,
          transportIdx: null,
          sectionIdx: null,
        };
        const element = document.getElementById('global-comment');
        element.scrollIntoView({ behavior: 'smooth' });
        return;
      }

      this.activeSection = { ...data };

      this.activeSection.globalComment = false;

      if (data) {
        const activeSectionString = this.sectionString(data);
        const element = document.getElementById(activeSectionString);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          console.log(activeSectionString);
        }
      }
    },

    sectionString(data) {
      const siteIdx = data.siteIdx >= 0 ? data.siteIdx : 0;
      const transportIdx = data.transportIdx >= 0 ? data.transportIdx : 0;
      const sectionIdx = data.sectionIdx >= 0 ? data.sectionIdx : 0;

      const siteString = (
        data.siteIdx > 0 ? this.currentExportLayout.sites[siteIdx].name.replace(/ /g, '_') : this.$t('sections.mainSynthesis').replace(/ /g, '_')
      ).toLowerCase();
      const transportString = this.transportMode(this.currentExportLayout.sites[siteIdx].transports[transportIdx].type).key.toLowerCase();
      const sectionString = this.currentExportLayout.sites[siteIdx].transports[transportIdx].sections[sectionIdx].name.toLowerCase();

      console.log(`${siteString}-${transportString}-${sectionString}`);
      return `${siteString}-${transportString}-${sectionString}`;
    },

    getSections() {
      return [this.mainSynthesis, ...this.actualSites].reduce((acc, site) => {
        site.transports.forEach(transport => {
          transport.sections.forEach(section => {
            acc.push({
              id: section.id,
              comment: section.comment,
            });
          });
        });
        return acc;
      }, []);
    },

    saveExportLayout() {
      const id = this.$route.params.studyId;
      const payload = {
        sections: this.getSections(),
        final_comment: this.globalComment,
      };
      this.$store
        .dispatch('patchExportLayout', {
          id,
          payload,
        })
        .then(data => {
          console.log(data);
        });
    },

    printExport() {
      const id = this.$route.params.studyId;
      this.loading = true;
      this.$store.dispatch('updateCurrentModal', {
        component: 'ExportLoading',
        params: null,
      });
      this.$store
        .dispatch('printExport', { id, language: this.userLanguage })
        .then(() => {
          const interval = setInterval(() => {
            this.$store.dispatch('getExportFile', { id }).then(e => {
              this.downloadURI(e.url, e.filename);
              this.$store.dispatch('updateCurrentModal', {
                component: null,
                params: null,
              });
              clearInterval(interval);
            });
          }, 4000);
        })
        .catch(() => {
          this.loading = false;
          this.$store.dispatch('updateCurrentModal', {
            component: null,
            params: null,
          });
        });
    },

    downloadURI(uri, name) {
      const link = document.createElement('a');
      link.download = name;
      link.href = uri;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    editConfig() {
      this.$router.push({
        name: this.currentExport.type === ExportType.Short ? 'exportTransportMode' : 'exportFilters',
      });
    },

    uploadLogo(files) {
      this.error = false;

      if (files.length <= 0) return;

      this.logo = files[0];

      const reader = new FileReader();

      reader.addEventListener(
        'load',
        () => {
          this.logoUrl = reader.result;
          this.currentExport.logo_url = this.logoUrl;
          this.$store.dispatch('updateLogo', { logo: this.logoUrl });
        },
        false,
      );

      if (this.logo) {
        reader.readAsDataURL(this.logo);
      }

      this.$store.dispatch('uploadLogo', { id: this.study.id, file: this.logo });
    },

    deleteLogo() {
      this.loading = false;
      this.logo = null;
      this.logoUrl = null;
      this.$store.dispatch('updateLogo', { logo: null });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';
.configuration-view {
  position: relative;
  height: 100vh;
  background-color: #f9f9f9;
  margin-top: -50px;
  padding-top: 50px;
  box-sizing: border-box;
  display: flex;
}
.heading {
  background-color: #f9f9f9;
  h2 {
    padding-top: 36px;
    padding-bottom: 22px;
    font-weight: 900;
    font-size: 20px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: #3c3c3b;
    box-sizing: border-box;
    padding-left: 68px;
    cursor: pointer;
    i {
      font-size: 1.5em;
      color: #b1b2b4;
    }
  }
}
.aside-panel {
  height: 100%;
  width: 386px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  background-color: white;
  display: flex;
  flex-direction: column;
  z-index: 10;

  .panel-content {
    flex: 1;
    overflow: auto;
  }

  .panel-summary {
    padding-left: 39px;
    padding-right: 17px;

    .introduction-summary {
      .index-heading {
        display: flex;
        align-items: center;
        height: 48px;
        border-bottom: 1px solid #e3e3e3;
        cursor: pointer;

        .index-icon {
        }

        .index-title {
          flex: 1;
          padding-left: 30px;
          font-size: 16px;
          font-weight: 900;
          color: #3c3c3b;
        }

        .icon-container {
          display: flex;
          justify-content: center;
          align-items: center;

          &.reversed {
            transform: rotate(180deg);
          }
        }
      }
      .index-container {
        padding: 16px 0 16px 64px;
        border-bottom: 1px solid #e3e3e3;

        .index-item {
          display: flex;
          align-items: center;
          padding: 5px 0;
          font-size: 14px;
          // cursor: pointer;
          transition: all 0.3s ease;

          // &.active,
          // &:hover {
          //   color: #318EAF;
          //   font-weight: 600;
          // }
        }
      }
    }

    .global-comment {
      padding: 12px;
      font-size: 16px;
      font-weight: 900;
      color: #3c3c3b;
      cursor: pointer;
      transition: 0.2s;

      // background: #E3E3E3;
      border-top: 1px solid #e3e3e3;
      border-bottom: 1px solid #e3e3e3;

      // border-radius: 2px;
      margin: 12px 0;

      .icon-bubble {
        position: absolute;
        top: 6px;
        font-size: 24px;
        // margin: -10px 0;
      }

      &:hover {
        color: #318eaf;
      }
    }

    .info-box {
      position: relative;
      margin: 12px 0;
      font-size: 14px;
      background: #e3e3e3;
      color: colors.$dark-grey-color;
      padding: 12px;
      border-radius: 2px;

      &::after {
        position: absolute;
        content: '';
        display: block;
        top: -4px;
        right: 12px;
        width: 12px;
        height: 12px;
        border-radius: 2px 0 0 0;
        background: #e3e3e3;
        transform: rotate(45deg);
      }
    }
  }
  .cta-container {
    align-items: center;
    justify-content: center;
    height: 60px;
    box-shadow: 0 -1px 10px rgba(0, 0, 0, 0.1);
  }
}
.sub-view {
  position: static;
}
.preview-container {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .scroll-box {
    overflow: auto;
    flex: 1;
  }

  .comment-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 1;
    width: 100%;
    height: 160px;
    flex-shrink: 0;
    padding-right: 192px;
    box-sizing: border-box;
    background: colors.$white-color;
    border-top: solid 1px colors.$grey-color;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

    .comment-title-container {
      display: flex;
      color: #9d9c9c;
      font-weight: 500;
      font-size: 14px;
      text-align: left;
      margin: 8px auto;
      max-width: 148.5mm;
      width: 100%;

      .comment-title {
        width: 100%;
      }

      .comment-character-counter {
        flex-shrink: 0;

        .overflow {
          font-weight: 800;
          color: colors.$main-color;
        }
      }
    }

    .comment-area {
      box-sizing: border-box;
      margin: 0 auto;
      max-width: 148.5mm;
      width: 100%;
      height: 100%;
      border: none;
      color: #262631;
      font-size: 14px;
      line-height: 18px;
      font-family: 'Source Sans Pro';
      resize: none;

      &:focus {
        outline: none;
      }
    }
  }
}

.filter-panel {
  position: absolute;
  right: 0;
  top: 0;
  padding: 50px 0 0;
  width: 192px;

  @media (max-width: 1200px) {
    display: none;
  }

  .info-block {
    border-left: solid 1px rgba(#979797, 0.2);
    padding: 0 12px;
    margin: 48px 0 0;

    .title {
      color: #9d9c9c;
      font-weight: 600;
      font-size: 14px;
    }
    .text {
      margin: 4px 0 16px;
      font-size: 14px;
      color: #262631;
    }
    .logo-container {
      position: relative;
      margin: -12px 0 16px;

      width: 128px;
      height: 96px;

      .close {
        position: absolute;
        top: 2px;
        right: 2px;
        width: 16px;
        height: 16px;

        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 8px;
        color: white;

        border-radius: 24px;
        background: rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(2px) brightness(80%);
        cursor: pointer;
        transition: 0.2s;

        &:hover {
          transform: scale(1.05);
        }

        &:active {
          transform: scale(0.9);
        }
      }

      .logo {
        width: 100%;
        height: 100%;

        border-radius: 2px;

        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
      }

      .logo-input-container {
        width: 100%;
        height: 100%;

        box-sizing: border-box;
        border: dashed 2px #e8e7e6;
        color: #3c3c3b;
        font-size: 14px;
        border-radius: 2px;

        transition: all 0.3s ease;

        &:not(.filled):hover {
          border-color: colors.$main-color;
        }

        .add-logo-label {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 12px;
          opacity: 0.8;
        }

        .logo-input {
          position: absolute;
          width: 100%;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          opacity: 0;

          cursor: pointer;
        }
      }
    }
  }
}

.report {
  background: #f9f9f9;
  width: 200%;
  margin: -50%;
  padding-right: 384px;
  padding-bottom: 640px;
  transform: scale(0.5);
  box-sizing: border-box;

  @media (max-width: 1200px) {
    padding-top: 0;
    padding-right: 0;
  }
}
</style>
